import { useFormik } from 'formik'
import Grid from '@mui/material/Grid'
import { toast } from 'react-toastify'
import Button from "@mui/material/Button"
import * as api from '../../../../services/api'
import { useParams, useNavigate } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import TextField from '@mui/material/TextField'
import { useCore } from "../../../../hooks/useCore"
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { LoadingPage } from "../../../../components/LoadingPage"
import JsonCodeEditor from "../../../../components/JsonCodeEditor"
import {
  Alert, Checkbox, Collapse, FormControlLabel, FormGroup, Input, InputAdornment,
  ListItemButton, ListItemText, Typography, IconButton, Tooltip
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SearchIcon from '@mui/icons-material/Search'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  RetrieveReportTemplateProps, ListMetadataReportTemplateProps, MetadataApiProps
} from '../../../../types/reportTemplate'

interface Values {
  name: string
  version: string
  is_active: boolean
  config: string
}

const initialValues: Values = {
  name: '',
  version: '',
  is_active: true,
  config: JSON.stringify({ blocks: [] }, null, 2)
}

export default function CreateOrUpdateReportTemplate(): JSX.Element {
  let { id } = useParams()
  const navigate = useNavigate()
  const { setTitleBar, setPathTitleBar } = useCore()

  const [loading, setLoading] = useState<boolean>(!!id)
  const [saving, setSaving] = useState<boolean>(false)
  const [configError, setConfigError] = useState<string>('')
  const [metadata, setMetadata] = useState<MetadataApiProps[]>([])
  const [metadataOpen, setMetadataOpen] = useState<boolean>(false)
  const [metadataSearch, setMetadataSearch] = useState<string>('')
  const [expandedApiId, setExpandedApiId] = useState<string | null>(null)

  const filteredMetadata = useMemo(() => {
    const term = metadataSearch.trim().toLowerCase()
    if (!term) return metadata
    return metadata.filter((item) =>
      item.name.toLowerCase().includes(term) || item.provider_name.toLowerCase().includes(term)
    )
  }, [metadata, metadataSearch])

  function copyApiId(apiId: string) {
    navigator.clipboard.writeText(apiId)
    toast.success("api_id copiado!", { position: toast.POSITION.TOP_RIGHT, autoClose: 1200 })
  }

  function slugify(text: string): string {
    return text
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
  }

  // esqueleto de bloco a partir da structure de uma API — reaproveitável entre
  // templates: cole o resultado, troque só api_id/response_block quando for
  // remapear o mesmo bloco pra outra fonte.
  // ordem das chaves = ordem de cima pra baixo no card renderizado (id/title são
  // só identidade; section/status/render vêm antes de source/fields de propósito).
  function buildBlockSkeleton(apiId: string, responseBlock: string, fields: Record<string, any>) {
    return {
      id: slugify(responseBlock),
      title: responseBlock,
      status: 'active',
      section: '',
      render: 'key_value_grid',
      source: { type: 'api', primary: { api_id: apiId, response_block: responseBlock } },
      fields: Object.keys(fields).map((key) => ({ key, label: key, format: 'text' })),
    }
  }

  function copyBlockSkeleton(apiId: string, responseBlock: string, fields: Record<string, any>) {
    const block = buildBlockSkeleton(apiId, responseBlock, fields)
    navigator.clipboard.writeText(JSON.stringify(block, null, 2))
    toast.success("Bloco copiado! Cole em blocks[] no config.", { position: toast.POSITION.TOP_RIGHT })
  }

  function insertBlockSkeleton(apiId: string, responseBlock: string, fields: Record<string, any>) {
    let parsed: Record<string, any>
    try {
      parsed = JSON.parse(values.config)
    } catch (e: any) {
      setConfigError(`JSON inválido: ${e.message}`)
      toast.error("Corrija o JSON do config antes de inserir um bloco.", { position: toast.POSITION.TOP_RIGHT })
      return
    }
    if (!Array.isArray(parsed.blocks)) parsed.blocks = []
    parsed.blocks.push(buildBlockSkeleton(apiId, responseBlock, fields))
    setFieldValue('config', JSON.stringify(parsed, null, 2))
    toast.success("Bloco inserido no config.", { position: toast.POSITION.TOP_RIGHT, autoClose: 1500 })
  }

  const { handleChange, handleSubmit, values, errors, setFieldValue, touched } = useFormik({
    initialValues,
    onSubmit: createOrUpdateReportTemplate,
  })

  function formatConfig() {
    try {
      const parsed = JSON.parse(values.config)
      setFieldValue('config', JSON.stringify(parsed, null, 2))
      setConfigError('')
    } catch (e: any) {
      setConfigError(`JSON inválido: ${e.message}`)
    }
  }

  async function previewReportTemplate() {
    let parsedConfig: Record<string, any>
    try {
      parsedConfig = JSON.parse(values.config)
    } catch (e: any) {
      setConfigError(`JSON inválido: ${e.message}`)
      toast.error("Corrija o JSON do config antes de gerar o preview.", { position: toast.POSITION.TOP_RIGHT })
      return
    }
    setConfigError('')

    const toastId = toast.loading("Gerando preview...")
    const response = await api.post(
      'product/report-template/preview/', { config: parsedConfig }, undefined, 'text'
    ) as { [key: string]: any }
    toast.dismiss(toastId)

    if (response.status === 200) {
      const blob = new Blob([response.data], { type: 'text/html' })
      const url = window.URL.createObjectURL(blob)
      const newWindow = window.open(url, '_blank')
      if (newWindow) newWindow.focus()
      setTimeout(() => window.URL.revokeObjectURL(url), 10000)
      return
    }

    let message = 'Erro ao gerar preview.'
    try {
      const parsedError = JSON.parse(response.data)
      if (Array.isArray(parsedError.config)) message = parsedError.config.join(' / ')
    } catch {
      // resposta não era JSON — mantém mensagem genérica
    }
    setConfigError(message)
    toast.error(message, { position: toast.POSITION.TOP_RIGHT })
  }

  async function createOrUpdateReportTemplate() {
    let parsedConfig: Record<string, any>
    try {
      parsedConfig = JSON.parse(values.config)
    } catch (e: any) {
      setConfigError(`JSON inválido: ${e.message}`)
      return
    }
    setConfigError('')

    const data = {
      name: values.name,
      version: values.version,
      is_active: values.is_active,
      config: parsedConfig,
    }

    setSaving(true)

    if (id) {
      const toastId = toast.loading("Atualizando...", { position: toast.POSITION.TOP_RIGHT })
      const response = await api.put(`product/report-template/update/${id}/`, data) as {[key:string]: any}
      toast.dismiss(toastId)

      if (response.status === 200) {
        toast.success("Template atualizado com sucesso!", { position: toast.POSITION.TOP_RIGHT })
      } else {
        handleSaveError(response)
      }
    } else {
      const toastId = toast.loading("Cadastrando...", { position: toast.POSITION.TOP_RIGHT })
      const response = await api.post('product/report-template/create/', data) as {[key:string]: any}
      toast.dismiss(toastId)

      if (response.status === 201) {
        toast.success("Template cadastrado com sucesso!", { position: toast.POSITION.TOP_RIGHT })
        navigate(`/report-template/${response.data.content.id}`)
      } else {
        handleSaveError(response)
      }
    }

    setSaving(false)
  }

  function handleSaveError(response: {[key:string]: any}) {
    const data = response?.data || {}
    const configErrors = data.config
    if (Array.isArray(configErrors) && configErrors.length > 0) {
      setConfigError(configErrors.join(' / '))
      toast.error("Ops.. Tivemos um problema, verifique os erros do config abaixo.", {
        position: toast.POSITION.TOP_RIGHT
      })
      return
    }

    // erro não é do config (ex.: name/version duplicados, non_field_errors) — mostra ele mesmo
    const otherErrors = Object.entries(data)
      .filter(([key]) => key !== 'config')
      .flatMap(([key, value]) => (Array.isArray(value) ? value : [value]).map((msg) => `${key}: ${msg}`))
    toast.error(otherErrors.length > 0 ? otherErrors.join(' / ') : "Ops.. Tivemos um problema ao salvar.", {
      position: toast.POSITION.TOP_RIGHT
    })
  }

  const getTemplate = async () => {
    try {
      const response = await api.get(`product/report-template/retrieve/${id}/`) as RetrieveReportTemplateProps
      if (!response?.content) throw new Error('Template não encontrado.')
      setFieldValue('name', response.content.name)
      setFieldValue('version', response.content.version)
      setFieldValue('is_active', response.content.is_active)
      setFieldValue('config', JSON.stringify(response.content.config, null, 2))
    } catch (e) {
      toast.error("Não foi possível carregar o template.", { position: toast.POSITION.TOP_RIGHT })
    } finally {
      setLoading(false)
    }
  }

  const getMetadata = async () => {
    const response = await api.get('product/report-template/metadata/') as ListMetadataReportTemplateProps
    setMetadata(response.content)
  }

  useEffect(() => {
    if (id) {
      setTitleBar('Editar template de extrato')
      getTemplate().catch(console.error)
    } else {
      setTitleBar('Novo template de extrato')
    }
    getMetadata().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    setPathTitleBar(undefined)
  }, [setPathTitleBar])

  return (
    <>
      <ToastContainer />
      {
        !loading
          ? (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} alignItems="center" justifyContent="center">
                <Grid item md={12} xs={12}>
                  <h3>Dados básicos</h3>
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    required
                    id="name"
                    name="name"
                    label="Nome"
                    value={values.name}
                    onChange={handleChange}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    required
                    id="version"
                    name="version"
                    label="Versão"
                    value={values.version}
                    onChange={handleChange}
                    error={touched.version && Boolean(errors.version)}
                    helperText={touched.version && errors.version}
                  />
                </Grid>
                <Grid item md={12} xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="is_active"
                          onChange={(e) => setFieldValue("is_active", e.target.checked)}
                          checked={values.is_active}
                        />
                      }
                      label="Template ativo"
                    />
                  </FormGroup>
                </Grid>

                <Grid item md={12} xs={12}>
                  <h3>Config (JSON)</h3>
                  <p>Estrutura de blocos do extrato — schema_version, header, legend, blocks, footer.</p>
                  <p>
                    Por bloco: <code>disclaimer</code> (aviso exibido no rodapé do card),{' '}
                    <code>value_color_rules</code> (cor do valor já traduzido, ex.: {'{'}"value": "Com dano", "color": "red"{'}'}).
                    Por campo em <code>fields[]</code>: <code>translate</code> (+ <code>translate_options</code>),{' '}
                    <code>format</code> (text/date/currency/number), <code>mask</code> (ex.: <code>##.###.###/####-##</code>),{' '}
                    <code>max_length</code>. Use o botão Preview para conferir o resultado antes de salvar.
                  </p>
                </Grid>
                <Grid item md={12} xs={12}>
                  <JsonCodeEditor
                    id="config"
                    value={values.config}
                    onChange={(value) => setFieldValue('config', value)}
                    error={!!configError}
                    minHeight="400px"
                  />
                </Grid>
                {configError && (
                  <Grid item md={12} xs={12}>
                    <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>{configError}</Alert>
                  </Grid>
                )}
                <Grid item md={12} xs={12}>
                  <Button variant="outlined" type="button" onClick={formatConfig}>
                    Formatar JSON
                  </Button>
                  {' '}
                  <Button variant="outlined" type="button" onClick={previewReportTemplate}>
                    Preview
                  </Button>
                </Grid>

                <Grid item md={12} xs={12}>
                  <ListItemButton onClick={() => setMetadataOpen(!metadataOpen)} sx={{ mt: 2 }}>
                    <ListItemText primary={`APIs disponíveis (${metadata.length}) — referência para api_id / structure`} />
                    {metadataOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>
                  <Collapse in={metadataOpen} timeout="auto" unmountOnExit>
                    <Input
                      fullWidth
                      placeholder="Buscar API por nome ou fornecedor..."
                      sx={{ px: 2, py: 1 }}
                      startAdornment={<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>}
                      value={metadataSearch}
                      onChange={(e) => setMetadataSearch(e.target.value)}
                    />
                    <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid #eee' }}>
                      {filteredMetadata.map((item) => {
                        const isOpen = expandedApiId === item.id
                        return (
                          <div key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                            <ListItemButton onClick={() => setExpandedApiId(isOpen ? null : item.id)}>
                              <ListItemText
                                primary={`${item.name} (${item.provider_name})`}
                                secondary={item.id}
                              />
                              <Tooltip title="Copiar api_id">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); copyApiId(item.id) }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <div style={{ padding: '0 16px 16px' }}>
                                {item.structure && Object.keys(item.structure).length > 0 ? (
                                  Object.entries(item.structure).map(([responseBlock, fields]) => (
                                    <div key={responseBlock} style={{ marginBottom: 12 }}>
                                      <Grid container alignItems="center" spacing={1}>
                                        <Grid item xs>
                                          <Typography variant="body2">
                                            <strong>{responseBlock}</strong> — {Object.keys(fields as object).length} campo(s)
                                          </Typography>
                                        </Grid>
                                        <Grid item>
                                          <Button
                                            size="small"
                                            onClick={() => copyBlockSkeleton(item.id, responseBlock, fields as Record<string, any>)}
                                          >
                                            Copiar bloco
                                          </Button>
                                        </Grid>
                                        <Grid item>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => insertBlockSkeleton(item.id, responseBlock, fields as Record<string, any>)}
                                          >
                                            Inserir no config
                                          </Button>
                                        </Grid>
                                      </Grid>
                                      <Typography variant="caption" color="text.secondary">
                                        {Object.keys(fields as object).join(', ')}
                                      </Typography>
                                    </div>
                                  ))
                                ) : (
                                  <Typography variant="caption" color="text.secondary">Sem structure cadastrada.</Typography>
                                )}
                              </div>
                            </Collapse>
                          </div>
                        )
                      })}
                      {filteredMetadata.length === 0 && (
                        <Typography sx={{ p: 2 }} color="text.secondary">Nenhuma API encontrada.</Typography>
                      )}
                    </div>
                  </Collapse>
                </Grid>

                <Grid item md={12} xs={12} textAlign="center" sx={{ mt: 2 }}>
                  <Button variant="contained" size="large" color="success" type="submit" disabled={saving}>
                    {id ? 'Atualizar' : 'Cadastrar'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )
          : <LoadingPage />
      }
    </>
  )
}
